import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import { Activity, TrendingUp, BarChart3, LineChart, Zap, Cpu, ArrowRight } from 'lucide-react'

type ModelSpec = { id: string; family: string; requires_gpu: boolean; available?: boolean }
type ModelsResp = {
  var: { id: string; name: string; gpu: boolean }[]
  stress: { id: string; name: string; gpu: boolean }[]
  universes: string[]
  gpu_models: string[]
  gpu_available: boolean
  bridge: { configured: boolean; url?: string; healthy?: boolean; gpu_device?: string; torch_version?: string; error?: string }
  bridge_models: ModelSpec[]
}

export default function RiskOverview() {
  const { data, isLoading } = useQuery<ModelsResp>({
    queryKey: ['risk-stats-models'],
    queryFn: () => api.get('/risk-stats/models').then(r => r.data),
  })

  const tiles = [
    { to: '/risk-stats/var',          name: 'Value at Risk',     desc: 'Run multiple VaR engines side-by-side across horizons & confidences.',  icon: TrendingUp },
    { to: '/risk-stats/stress',       name: 'Stress Testing',    desc: 'Scenario × model matrix with causal-graph propagation.',                icon: Activity },
    { to: '/risk-stats/factor-model', name: 'Factor Model',      desc: 'Orthogonalised Gram-Schmidt factor model with t-stats and p-values.',   icon: BarChart3 },
    { to: '/risk-stats/runs',         name: 'Run Registry',      desc: 'Every run is tagged with git sha, config hash, runtime, and status.',   icon: LineChart },
  ]

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Statistical Risk</h1>
        <p className="text-muted-foreground">
          Model-driven risk surface powered by <span className="font-mono">ft.quant.risk</span> — VaR, stress, factors, ensembles.
        </p>
      </div>

      {/* Bridge / GPU status */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-3 mb-2">
          <Cpu className="h-5 w-5" />
          <h2 className="font-semibold">Compute topology</h2>
        </div>
        {isLoading ? (
          <div className="spinner" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="border border-border rounded p-3">
              <div className="text-xs uppercase text-muted-foreground mb-1">Local (CPU)</div>
              <div className="font-medium">Plutus API container</div>
              <div className="text-muted-foreground text-xs mt-1">
                {(data?.var.length ?? 0) - (data?.gpu_models.length ?? 0)} VaR engines · {(data?.stress.length ?? 0) - 1} stress engines
              </div>
            </div>
            <div className="border border-border rounded p-3">
              <div className="text-xs uppercase text-muted-foreground mb-1">GPU bridge</div>
              <div className="font-medium">
                {data?.bridge.configured
                  ? (data.bridge.healthy ? 'Online' : 'Configured but unhealthy')
                  : 'Not configured'}
              </div>
              <div className="text-muted-foreground text-xs mt-1 break-all">
                {data?.bridge.url || '—'} {data?.bridge.gpu_device ? `· ${data.bridge.gpu_device}` : ''}
              </div>
              {data?.bridge.error && <div className="text-destructive text-xs mt-1">{data.bridge.error}</div>}
              {!data?.bridge.configured && (
                <div className="text-xs text-muted-foreground mt-2 leading-relaxed">
                  Set <span className="font-mono">FT_RISK_BRIDGE_URL</span> + <span className="font-mono">FT_RISK_BRIDGE_API_KEY</span> on the api container.
                  See the <span className="font-mono">ft.risk.bridge/deploy/</span> folder for installers.
                </div>
              )}
            </div>
            <div className="border border-border rounded p-3">
              <div className="text-xs uppercase text-muted-foreground mb-1">GPU engines</div>
              <div className="font-medium">
                {data?.gpu_available ? 'Ready' : 'Refused (no bridge)'}
              </div>
              <div className="text-muted-foreground text-xs mt-1">
                {(data?.gpu_models ?? []).join(', ') || '—'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Section tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {tiles.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            className="block bg-card border border-border rounded-lg p-5 hover:border-primary transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <t.icon className="h-6 w-6 text-primary mt-0.5" />
                <div>
                  <div className="font-semibold">{t.name}</div>
                  <p className="text-sm text-muted-foreground mt-1">{t.desc}</p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </Link>
        ))}
      </div>

      {/* Model catalog */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h2 className="font-semibold mb-3 flex items-center gap-2"><Zap className="h-5 w-5" /> Available engines</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs uppercase text-muted-foreground mb-2">VaR</div>
            <div className="flex flex-wrap gap-2">
              {data?.var.map((m) => (
                <span
                  key={m.id}
                  className={
                    'text-xs px-2 py-1 rounded border ' +
                    (m.gpu
                      ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300'
                      : 'border-border bg-muted')
                  }
                  title={m.gpu ? 'GPU-only (routed via ft.risk.bridge)' : 'CPU, runs locally'}
                >
                  {m.id}{m.gpu && ' · GPU'}
                </span>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted-foreground mb-2">Stress</div>
            <div className="flex flex-wrap gap-2">
              {data?.stress.map((m) => (
                <span
                  key={m.id}
                  className={
                    'text-xs px-2 py-1 rounded border ' +
                    (m.gpu
                      ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300'
                      : 'border-border bg-muted')
                  }
                >
                  {m.id}{m.gpu && ' · GPU'}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
